import { useState } from "react";
import { useTranslations } from "use-intl";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import FlightSummary from "@/app/ui/flight-purchase/FlightSummary";
import { TravellersListSummary } from "@/app/ui/flight-purchase/TravellersListSummary";
import Summary from "@/app/ui/flight-purchase/Summary";
import BottomDrawer from "@/app/ui/flight-purchase/BottomDrawer";
import MoreDetailsDrawer from "@/app/ui/flight-purchase/MoreDetailsDrawer/MoreDetailsDrawer";
import { AnimatedLoader } from "@/app/ui/AnimatedLoader";
import PageLoader from "@/app/ui/PageLoader";
import { useFlightBooking } from "@/app/lib/hooks/useFlightBooking";
import { transformFlightTraveler } from "@/app/lib/utils/transformTraveler";
import { usePurchaseStateContext } from "@/app/lib/context/PurchaseStateContext";
import { BookingError } from "@/app/ui/errors/BookingError";
import { PurchaseStatus } from "@/app/lib/payments/types";
import { FlightTrackingEvents, rudderAnalytics } from "@/app/lib/rudderstack";
import {
  flightAvailabilityQueryOptions,
  useFlightAvailabilityMutation,
} from "@/app/lib/hooks/useFlightAvailability";
import { FlightOfferError } from "@/app/ui/flight-purchase/FlightOfferError";
import { PriceChangedModal } from "@/app/ui/PriceChangedModal";
import { formatCurrency } from "@/app/lib/utils/currency";
import type { GetFlightAvailabilityTokenOutputDto } from "@/app/lib/types/codegen";
import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import { OTPPopup } from "@/app/ui/OTPPopup";
import InstallmentDetails from "@/app/ui/flight-purchase/InstallmentDetails";
import { PaymentDetails } from "@/app/ui/flight-purchase/PaymentDetails";
import {
  includeBnplFee,
  calculateMonthlyInstallment,
} from "@/app/lib/utils/bnpl-fee";
import { useFlightSelectionPayloadContext } from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useTravellersSelectionContext } from "@/app/lib/context/TravellersSelectionContext";
import { PromoCodeForm } from "@/app/ui/PromoCodeForm";
import { Feature, isFeatureEnabled } from "@/app/lib/utils/featureFlags";
import { usePromoCodeValidation } from "@/app/lib/hooks/usePromoCodeValidation";
import { BannersPlacement } from "@/app/ui/banners-placement/BannersPlacement";
import { BannersPlacementSlug } from "@/app/lib/types/enums";

export const Route = createFileRoute(
  "/_authenticated/_flights/_flight-offer/flights/offer/$id/step/3",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const t = useTranslations();
  const navigate = Route.useNavigate();

  const { paymentType, sessionId, selectedInstallments, refreshSession } =
    usePaymentContext();
  const { updatePurchaseState, purchaseState } = usePurchaseStateContext();
  const { selectedFlightPayload, promoCode, setPromoCode } =
    useFlightSelectionPayloadContext();
  const { data: singleFlightDetails, isLoading: isAdditionalDataLoading } =
    useQuery(flightAvailabilityQueryOptions(selectedFlightPayload));
  const { selectedTravellers } = useTravellersSelectionContext();

  const [termsAndConditions, setTermsAndConditions] = useState(false);
  const [moreDetailsOpen, setMoreDetailsOpen] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showOTP, setShowOTP] = useState(false);

  // TODO: what if price changes before the booking?
  // TODO: pass code to purchase
  const { data: promoCodeData, validationStatus: promoCodeValidationStatus } =
    usePromoCodeValidation({
      service: "flights",
      validationRequestData: {
        code: promoCode || "",
        availabilityToken: singleFlightDetails?.availabilityToken || "",
      },
    });

  const originalPrice = singleFlightDetails?.totalPrice || 0;
  const includedBnplFee = includeBnplFee(originalPrice);
  const totalPrice = paymentType === "bnpl" ? includedBnplFee : originalPrice;
  const totalPriceWithPromoCode = promoCodeData?.finalPrice || totalPrice;
  const paymentMethod = paymentType === "bnpl" ? "bnpl" : "qiAccount";

  const { qiPayment, checkoutPayment, bnplPayment } = useFlightBooking({
    onSuccess: ({ orderId, status }) => {
      if (status === PurchaseStatus.SUCCESSFUL) {
        if (paymentType === "bnpl") {
          // to update eligibility info
          refreshSession();
        }

        navigate({
          to: "/flights/offer/$id/step/final",
          search: { orderId },
        }).then(() => {
          updatePurchaseState("completed");
        });
      }
    },
  });

  const handleRefetchAvailabilitySuccess = (
    data: GetFlightAvailabilityTokenOutputDto,
  ) => {
    if (data.data) {
      const latestTotalPrice = data.data.totalPrice;
      if (latestTotalPrice !== originalPrice) {
        setShowConfirmation(true);
      } else {
        handlePurchase(singleFlightDetails?.availabilityToken);
      }
    }
  };

  const {
    data: availability,
    isError: isAvailabilityError,
    error: availabilityError,
    isPending: isAvailabilityRefetching,
    mutate: refetchAvailability,
  } = useFlightAvailabilityMutation({
    onSuccess: handleRefetchAvailabilitySuccess,
  });

  const handlePurchase = (availabilityToken?: string) => {
    if (
      !selectedFlightPayload ||
      purchaseState === "completed" ||
      !availabilityToken
    ) {
      return;
    }

    rudderAnalytics.track(FlightTrackingEvents.USER_PROCEED_TO_PAYMENT_SCREEN, {
      amount: totalPriceWithPromoCode,
      payment_method: paymentMethod,
      currency: singleFlightDetails?.currency,
    });

    if (paymentMethod === "bnpl") {
      setShowOTP(true);
    } else if (paymentMethod === "qiAccount") {
      qiPayment.mutateAsync({
        offerId: selectedFlightPayload?.index,
        searchResultKey: selectedFlightPayload?.searchResultKey,
        searchResultToken: selectedFlightPayload?.searchResultToken,
        itineraryIndex: selectedFlightPayload?.itineraryIndex,
        availabilityToken,
        travelers: selectedTravellers.map((traveller) =>
          transformFlightTraveler(traveller),
        ),
      });
    } else {
      checkoutPayment.submit({
        offerId: selectedFlightPayload?.index,
        searchResultKey: selectedFlightPayload?.searchResultKey,
        searchResultToken: selectedFlightPayload?.searchResultToken,
        itineraryIndex: selectedFlightPayload?.itineraryIndex,
        availabilityToken,
        travelers: selectedTravellers.map((traveller) =>
          transformFlightTraveler(traveller),
        ),
      });
    }
  };

  const handleOTPSubmit = (otp: string) => {
    if (
      !selectedFlightPayload ||
      !singleFlightDetails?.availabilityToken ||
      !sessionId
    ) {
      return;
    }

    bnplPayment.mutateAsync({
      offerId: selectedFlightPayload.index,
      searchResultKey: selectedFlightPayload.searchResultKey,
      searchResultToken: selectedFlightPayload.searchResultToken,
      itineraryIndex: selectedFlightPayload.itineraryIndex,
      availabilityToken: singleFlightDetails.availabilityToken,
      travelers: selectedTravellers.map((traveller) =>
        transformFlightTraveler(traveller),
      ),
      sessionId,
      otp,
    });
  };

  const handleCancelPriceChange = () => {
    setShowConfirmation(false);
    navigate({
      to: "/",
      search: { category: "flights" },
    });
  };

  if (isAvailabilityError)
    return <FlightOfferError error={availabilityError} />;

  if (qiPayment.isError) {
    return <BookingError error={qiPayment.error} type="flights" />;
  }

  if (
    qiPayment.isPending ||
    checkoutPayment.isPending ||
    bnplPayment.isPending
  ) {
    return (
      <div className="h-96">
        <PageLoader>
          <AnimatedLoader icon="airplane" />
          <h3 className="mt-3 font-normal text-text-primary">
            {t("flights.bookingFlight")}
          </h3>
        </PageLoader>
      </div>
    );
  }

  return (
    <>
      <div className="px-4">
        <FlightSummary selectedFlight={singleFlightDetails} />

        {selectedTravellers.length > 0 && (
          <>
            <div className="section-title mt-4">
              {t("travellers.travellersList")}
            </div>
            <div className="section-card">
              <TravellersListSummary selectedTravellers={selectedTravellers} />
            </div>
          </>
        )}

        <BannersPlacement
          slug={BannersPlacementSlug.CHECKOUT}
          classNames={{
            wrapper: "-mx-4 mt-4",
          }}
        />

        {isFeatureEnabled(Feature.PromoCodes) ? (
          <div className="mt-4">
            <PromoCodeForm
              appliedCode={promoCode || ""}
              validationStatus={promoCodeValidationStatus}
              onCodeApply={({ code }) => {
                setPromoCode(code);
              }}
              onCodeRemove={() => {
                setPromoCode(null);
              }}
            />
          </div>
        ) : null}

        <Summary
          termsAndConditions={termsAndConditions}
          onTermsChange={setTermsAndConditions}
          onMoreDetails={() => {
            setMoreDetailsOpen(true);
          }}
        />
        {paymentMethod === "bnpl" ? (
          <InstallmentDetails
            promoCodeDiscount={promoCodeData?.discount}
            totalPrice={totalPriceWithPromoCode}
          />
        ) : (
          <PaymentDetails
            promoCodeDiscount={promoCodeData?.discount}
            totalPrice={totalPriceWithPromoCode}
            paymentMethod={paymentMethod}
          />
        )}
      </div>

      <PriceChangedModal
        open={showConfirmation}
        price={formatCurrency(
          availability?.data.data.totalPrice,
          availability?.data.data.currency,
        )}
        onCancel={handleCancelPriceChange}
        onConfirm={() => {
          handlePurchase(availability?.data.data.availabilityToken);
        }}
      />

      <MoreDetailsDrawer
        open={moreDetailsOpen}
        onClose={() => setMoreDetailsOpen(false)}
      />

      <BottomDrawer
        totalPrice={
          paymentType === "bnpl"
            ? undefined
            : formatCurrency(totalPriceWithPromoCode)
        }
        payLaterPrice={
          paymentType === "bnpl"
            ? formatCurrency(
                calculateMonthlyInstallment(
                  includedBnplFee,
                  selectedInstallments,
                ),
              )
            : undefined
        }
        buttonLabel={t("flights.purchaseTicket")}
        onButtonClick={() => {
          if (selectedFlightPayload) {
            refetchAvailability(selectedFlightPayload);
          }
        }}
        disabled={
          !termsAndConditions ||
          isAdditionalDataLoading ||
          promoCodeValidationStatus?.type === "loading"
        }
        loading={isAvailabilityRefetching}
      />

      <OTPPopup
        isOpen={showOTP}
        onClose={() => setShowOTP(false)}
        onSubmit={handleOTPSubmit}
        error={bnplPayment.error ? t("payment.otpError") : undefined}
      />
    </>
  );
}
